import { useMemo, useRef, useSyncExternalStore } from "react";
import { useQuery } from "@tanstack/react-query";
import { subDays } from "date-fns";
import type { Octokit } from "@octokit/rest";
import type {
  UseRunsOptions,
  UseRunsResult,
  WindowDays,
  Workflow,
  WorkflowRun,
} from "@/lib/github/types";
import { createOctokit } from "@/lib/github/client";
import { paginateWithTimeSlice, type PaginateResult } from "@/lib/github/pagination";

export const WINDOW_DAYS_OPTIONS = [7, 30, 45, 90, 180, 400] as const satisfies readonly WindowDays[];

const DEFAULT_WINDOW_DAYS: WindowDays = 7;

interface ProgressState {
  loaded: number;
  /** Best known upper bound (first full-window total_count), or null until the first response. */
  total: number | null;
  /** ISO day (YYYY-MM-DD) of the slice currently being fetched, null when idle. */
  currentDate: string | null;
}

const IDLE_PROGRESS: ProgressState = { loaded: 0, total: null, currentDate: null };

/**
 * Minimal external store for pagination progress (count + total + current slice day).
 * Subscribers are notified only when a value actually changes.
 * Using this keeps progress updates isolated — only the ProgressBanner
 * re-renders, not the entire Dashboard.
 */
function createProgressStore() {
  let value: ProgressState = IDLE_PROGRESS;
  const subscribers = new Set<() => void>();
  function notify() {
    for (const sub of subscribers) sub();
  }
  return {
    set(loaded: number, currentDate: string | null, total: number | null) {
      if (
        loaded === value.loaded &&
        currentDate === value.currentDate &&
        total === value.total
      ) {
        return;
      }
      value = { loaded, total, currentDate };
      notify();
    },
    reset() {
      if (value === IDLE_PROGRESS) return;
      value = IDLE_PROGRESS;
      notify();
    },
    subscribe(cb: () => void) {
      subscribers.add(cb);
      return () => {
        subscribers.delete(cb);
      };
    },
    getSnapshot() {
      return value;
    },
  };
}

export function useOctokit(token: string | null): Octokit | null {
  return useMemo(() => (token ? createOctokit(token) : null), [token]);
}

export function useWorkflows(
  octokit: Octokit | null,
  owner: string,
  repo: string,
) {
  return useQuery<Workflow[]>({
    queryKey: ["workflows", owner, repo],
    enabled: octokit != null && owner.length > 0 && repo.length > 0,
    queryFn: async () => {
      if (!octokit) throw new Error("no octokit");
      const workflows: Workflow[] = [];
      let page = 1;
      // /workflows has no cap; paginate simply.
      while (true) {
        const res = await octokit.rest.actions.listRepoWorkflows({
          owner,
          repo,
          per_page: 100,
          page,
        });
        workflows.push(...(res.data.workflows as Workflow[]));
        if (res.data.workflows.length < 100) break;
        page += 1;
      }
      return workflows;
    },
  });
}

interface UseRunsWindowParams {
  octokit: Octokit | null;
  owner: string;
  repo: string;
  windowDays: WindowDays;
  hardCap: number;
  windowStart: Date;
  windowEnd: Date;
  /** Distinct queryKey prefix so current and previous windows never share a cache entry. */
  keyPrefix: "runs" | "runs-previous";
  /** When false, the hook is a no-op (enabled: false). */
  active: boolean;
}

/** Shared implementation for `useRuns` and `usePreviousRuns`. */
function useRunsWindow(params: UseRunsWindowParams): UseRunsResult {
  const { octokit, owner, repo, windowDays, hardCap, windowStart, windowEnd, keyPrefix, active } = params;

  // Progress store lives in a ref so it is stable across renders.
  // useSyncExternalStore makes only this hook (and ProgressBanner via props)
  // re-render on each page — the Dashboard component itself is unaffected.
  const storeRef = useRef(createProgressStore());
  const progress = useSyncExternalStore(
    storeRef.current.subscribe,
    storeRef.current.getSnapshot,
    storeRef.current.getSnapshot,
  );

  const query = useQuery<PaginateResult<WorkflowRun>>({
    queryKey: [keyPrefix, owner, repo, windowDays, hardCap],
    enabled: active && octokit != null && owner.length > 0 && repo.length > 0,
    // Consuming `signal` opts us into react-query's automatic cancellation:
    // when the queryKey changes (e.g. user switches repo) or the observer
    // unmounts, the signal is aborted and the paginator bails out.
    queryFn: async ({ signal }) => {
      if (!octokit) throw new Error("no octokit");
      storeRef.current.set(0, null, null);
      try {
        const result = await paginateWithTimeSlice<WorkflowRun>({
          octokit,
          route: "GET /repos/{owner}/{repo}/actions/runs",
          params: { owner, repo },
          since: windowStart,
          until: windowEnd,
          hardCap,
          onProgress: (n, day, total) =>
            storeRef.current.set(n, day ?? null, total ?? null),
          itemsKey: "workflow_runs",
          dayCache: { owner, repo },
          signal,
        });
        return result;
      } finally {
        // Clear the progress indicators once the fetch is done (any outcome,
        // including abort).
        storeRef.current.reset();
      }
    },
  });

  return {
    runs: query.data?.items,
    hitCap: query.data?.hitCap ?? false,
    hardCap,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    loaded: progress.loaded,
    total: progress.total,
    currentDate: progress.currentDate,
    windowStart,
    windowEnd,
  };
}

export function useRuns(
  octokit: Octokit | null,
  owner: string,
  repo: string,
  options: UseRunsOptions = {},
): UseRunsResult {
  const windowDays = options.windowDays ?? DEFAULT_WINDOW_DAYS;
  const windowEnd = useMemo(() => new Date(), []);
  const windowStart = useMemo(() => subDays(windowEnd, windowDays), [windowEnd, windowDays]);
  const hardCap = options.hardCap ?? Math.max(10000, windowDays * 50);
  return useRunsWindow({
    octokit,
    owner,
    repo,
    windowDays,
    hardCap,
    windowStart,
    windowEnd,
    keyPrefix: "runs",
    active: true,
  });
}

interface UsePreviousRunsOptions {
  windowDays: WindowDays;
  /** Optional override. Default: max(10000, windowDays * 50). */
  hardCap?: number;
}

/**
 * Fetches the window immediately preceding the current one (same duration).
 * When `options` is `null`, the hook is a no-op (enabled: false) — no fetch
 * is triggered. Pass `null` when compareMode is off to avoid wasted API calls.
 */
export function usePreviousRuns(
  octokit: Octokit | null,
  owner: string,
  repo: string,
  options: UsePreviousRunsOptions | null,
): UseRunsResult {
  const windowDays = options?.windowDays ?? DEFAULT_WINDOW_DAYS;
  // Previous window ends where the current window starts, same duration before that.
  const windowEnd = useMemo(() => subDays(new Date(), windowDays), [windowDays]);
  const windowStart = useMemo(() => subDays(windowEnd, windowDays), [windowEnd, windowDays]);
  const hardCap = options?.hardCap ?? Math.max(10000, windowDays * 50);
  return useRunsWindow({
    octokit,
    owner,
    repo,
    windowDays,
    hardCap,
    windowStart,
    windowEnd,
    keyPrefix: "runs-previous",
    active: options !== null,
  });
}

/**
 * Utility: run an async function over a list with bounded concurrency.
 * Used for fetching per-run jobs/timing for top 20 runs by tranches of 5.
 */
export async function mapConcurrent<T, U>(
  items: T[],
  fn: (item: T, idx: number) => Promise<U>,
  concurrency = 5,
): Promise<U[]> {
  const results: U[] = new Array(items.length);
  let cursor = 0;
  async function worker() {
    while (true) {
      const i = cursor++;
      if (i >= items.length) return;
      const item = items[i] as T;
      results[i] = await fn(item, i);
    }
  }
  const workers = Array.from({ length: Math.min(concurrency, items.length) }, () => worker());
  await Promise.all(workers);
  return results;
}
